/**
 * The privacy policy and the terms, in both languages, for all five apps.
 *
 * ## Why this is in `config/` and not in an app
 *
 * `config/` holds what is neither a component nor a token: plain data that more than one
 * app reads, with no DOM, no React, no `import.meta` and no bundler global — so
 * `app_mobile` can read it as it will read `tokens.ts`. Two things made it the right home
 * for these two documents rather than `src/data/` in each app:
 *
 *  - **There is one Foundation.** A privacy policy is a statement about what an
 *    organisation does with what it is told, and King of Kings does the same thing with a
 *    contact form on the resort's site as with one on the Foundation's. Five copies would
 *    become five policies the first time one was amended and the others were not, and
 *    nobody would notice, because nobody reads four of them.
 *  - **`app_steward` needs them too**, and it has no `src/data/seo.ts` and no public
 *    identity of its own. A shared module is the only place all five can reach.
 *
 * The editorial-copy exception in ground rule 8 says listings, member terms and scripture
 * stay in `src/data/` because translating them is a decision about tone. These are that
 * kind of text — which is why they are bilingual inline here rather than catalogue keys —
 * but they are not one app's, so they sit in the one module every app may import.
 *
 * ## What is in them, and what is deliberately not
 *
 * **Everything below describes what the platform does today**, and nothing below describes
 * something it might do. There is no analytics, so the policy does not mention analytics
 * in the abstract. There is no email provider yet, so it does not name one. The one third
 * party that genuinely processes a request is Cloudflare, and it is the only one named.
 *
 * Where a fact does not exist it **says so**, the way the Foundation's street address and
 * telephone number already do across the platform — a registered entity name and a data
 * controller's registered address are exactly the kind of thing a reader would act on, and
 * a plausible invention is worse than a stated gap.
 *
 * **Both documents open by saying they are a first draft awaiting the Foundation's review.**
 * That is true, and it is the honest state for a policy written from the code rather than
 * from a lawyer's instruction. Take the line out on the day somebody has read and approved
 * it, and not before.
 */

import type { Text } from '../i18n/text'

/** One heading and its paragraphs. `body` is a list so a section can run to more than one. */
export interface LegalSection {
  heading: Text
  body: Text[]
}

export interface LegalDocument {
  title: Text
  /**
   * ISO date, rendered by the page. A policy with no date cannot be told from a policy
   * nobody has looked at since the site launched.
   */
  updated: string
  /** The line above the sections, including the draft notice. */
  intro: Text[]
  sections: LegalSection[]
}

/** The one inbox on this platform that is real and monitored for both documents. */
const CONTACT_EMAIL = 'foundation@kkfmongolia.com'

const DRAFT_NOTICE: Text = {
  en: 'This is a first draft, written from what the platform actually does, and it is awaiting the Foundation’s review. If anything here is unclear or looks wrong, please write to us — the address is at the bottom.',
  mn: 'Энэ бол платформын бодитоор хийдэг зүйлээс бичсэн анхны хувилбар бөгөөд Сангийн хяналтыг хүлээж байна. Хэрэв энд ямар нэг зүйл тодорхойгүй эсвэл буруу санагдвал бидэнд бичнэ үү — хаяг доор байна.',
}

export const PRIVACY_POLICY: LegalDocument = {
  title: { en: 'Privacy Policy', mn: 'Нууцлалын бодлого' },
  updated: '2026-09-19',
  intro: [
    DRAFT_NOTICE,
    {
      en: 'This policy covers every King of Kings Foundation website: the Foundation’s own site, the resort, King of Kings Garage, King of Kings Partners, and the internal portal used by Foundation staff.',
      mn: 'Энэ бодлого нь King of Kings Foundation-ийн бүх вэбсайтад хамаарна: Сангийн өөрийн сайт, амралтын газар, King of Kings Garage, King of Kings Partners, мөн Сангийн ажилтнуудын ашигладаг дотоод портал.',
    },
  ],
  sections: [
    {
      heading: { en: 'Who we are', mn: 'Бид хэн бэ' },
      body: [
        {
          en: 'King of Kings Foundation, Ulaanbaatar, Mongolia. Our registered entity details and registered address are to be confirmed and will be stated here once they are; we would rather leave a gap than print something you might act on.',
          mn: 'King of Kings Foundation, Улаанбаатар, Монгол. Бүртгэлтэй байгууллагын мэдээлэл, бүртгэлтэй хаягийг тодруулах шаардлагатай бөгөөд тодорхой болмогц энд бичнэ; таны итгэж болохооргүй зүйл хэвлэхээс илүү хоосон орхих нь дээр гэж үзэж байна.',
        },
        {
          en: `You can reach us at ${CONTACT_EMAIL} about anything on this page.`,
          mn: `Энэ хуудсанд байгаа аливаа зүйлийн талаар ${CONTACT_EMAIL} хаягаар бидэнтэй холбогдож болно.`,
        },
      ],
    },
    {
      heading: { en: 'What we collect, and when', mn: 'Бид юуг, хэзээ цуглуулдаг вэ' },
      body: [
        {
          en: 'We only collect what you type into a form. There is no account to create as a visitor and nothing is gathered in the background.',
          mn: 'Бид зөвхөн таны маягтад бичсэн зүйлийг цуглуулдаг. Зочин хүн бүртгэл үүсгэх шаардлагагүй бөгөөд далд байдлаар юу ч цуглуулдаггүй.',
        },
        {
          en: 'A contact or enquiry form: your name, your email address, and what you wrote. A newsletter sign-up: your email address. An Partners application: your business, its trade and province, what you are willing to take someone on for, your name and position, your email address, and which language you would like us to write to you in. A gift on the Foundation’s donation page: the appeal and the amount, your email address, your name if you give it, whether you asked not to be named, anything you chose to write in the note, and the language the page was in. Nothing is charged on the site yet, so no card or bank details are asked for or kept.',
          mn: 'Холбоо барих эсвэл лавлагааны маягт: таны нэр, имэйл хаяг, бичсэн зүйл. Мэдээллийн товхимолд бүртгүүлэх: таны имэйл хаяг. Partners-д хүсэлт гаргах: таны бизнес, үйл ажиллагааны чиглэл, аймаг/хот, хэнийг ямар ажилд авахад бэлэн байгаа, таны нэр, албан тушаал, имэйл хаяг, мөн бидний танд ямар хэлээр бичихийг хүсэж буй. Сангийн хандивын хуудсаар хандив өгөх: уриалга ба дүн, таны имэйл хаяг, өгсөн бол таны нэр, нэрээ дурдуулахгүй байхыг хүссэн эсэх, тэмдэглэлд бичсэн зүйл, мөн хуудасны хэл. Сайтаар одоогоор төлбөр авдаггүй тул карт, банкны мэдээлэл асуудаггүй, хадгалдаггүй.',
        },
        {
          en: 'Foundation staff have accounts on the internal portal. Those hold a name, an email address, a telephone number if given, and the roles that decide what a member of staff can see.',
          mn: 'Сангийн ажилтнууд дотоод порталд бүртгэлтэй байдаг. Түүнд нэр, имэйл хаяг, өгсөн бол утасны дугаар, мөн ажилтан юу харахыг тодорхойлдог эрхүүд хадгалагдана.',
        },
      ],
    },
    {
      heading: { en: 'What we do not do', mn: 'Бидний хийдэггүй зүйл' },
      body: [
        {
          en: 'We run no analytics, no advertising, and no tracking of any kind. There are no third-party scripts on these sites and no cookie is set to follow you between them or anywhere else. We do not sell, rent or share what you tell us, and we do not build a profile of you.',
          mn: 'Бид ямар ч аналитик, сурталчилгаа, хяналт ажиллуулдаггүй. Эдгээр сайтад гуравдагч этгээдийн скрипт байхгүй бөгөөд таныг сайтуудын хооронд эсвэл өөр хаа нэгтээ дагах күүки тохируулдаггүй. Таны хэлсэн зүйлийг бид зардаггүй, түрээслүүлдэггүй, хуваалцдаггүй, мөн таны тухай профайл үүсгэдэггүй.',
        },
        {
          en: 'Saved listings on Partners are the one thing stored in your browser. They live on that device only, never reach us, and disappear if you clear your browser’s data. The page says so where you use it.',
          mn: 'Partners дээр хадгалсан зарууд бол таны хөтчид хадгалагддаг цорын ганц зүйл. Тэдгээр нь зөвхөн тухайн төхөөрөмж дээр байх бөгөөд бидэнд хүрдэггүй, хөтчийнхөө өгөгдлийг цэвэрлэвэл устана. Ашиглах газарт нь хуудас үүнийг хэлдэг.',
        },
      ],
    },
    {
      heading: { en: 'Who else handles it', mn: 'Өөр хэн үүнийг боловсруулдаг вэ' },
      body: [
        {
          en: 'Our sites are served through Cloudflare, so a request you make to us passes through their network and they see the ordinary things any network sees — the address you connected from, and which page you asked for. Cloudflare also tells the page which country a request came from, which is how a site can open in Mongolian for a reader whose browser did not say what language they read. No other company is involved, and nothing is sent anywhere for any other purpose.',
          mn: 'Манай сайтууд Cloudflare-ээр дамжин ажилладаг тул таны илгээсэн хүсэлт тэдний сүлжээгээр дамжих ба тэд ямар ч сүлжээний хардаг энгийн зүйлсийг хардаг — таны холбогдсон хаяг, ямар хуудас хүссэн. Мөн Cloudflare хүсэлт аль улсаас ирснийг хуудаст хэлдэг бөгөөд ингэснээр хөтөч нь ямар хэл уншдагаа хэлээгүй уншигчид зориулж сайт монголоор нээгддэг. Өөр ямар ч компани оролцдоггүй, өөр зорилгоор хаана ч юу ч илгээгддэггүй.',
        },
        {
          en: 'We do not yet use an email provider. When we begin sending email — a reply to an application, an invitation to an account — this page will name who sends it before that starts.',
          mn: 'Бид одоогоор имэйл үйлчилгээ үзүүлэгч ашигладаггүй. Имэйл илгээж эхлэх үед — хүсэлтэд өгөх хариу, бүртгэлийн урилга — эхлэхээс өмнө хэн илгээхийг энэ хуудсанд бичнэ.',
        },
      ],
    },
    {
      heading: { en: 'How long we keep it', mn: 'Бид үүнийг хэр удаан хадгалдаг вэ' },
      body: [
        {
          en: 'An enquiry is kept while we are dealing with it and for as long as it is useful to remember the conversation. An Partners application is kept while the business is listed, because the listing is what it became. A record of a gift is kept for as long as the Foundation must keep records of what it was given — a gift is receipted, and for a donor who pays tax in the US it is a line on a return — and a note you wrote is kept with it. Every change to that record is logged, and the log is kept as long as the record. A staff account is kept while that person works with the Foundation. Ask us to delete any of it and we will, unless we are required to keep it.',
          mn: 'Лавлагааг бид шийдвэрлэж байх хугацаанд, мөн яриаг санахад хэрэгтэй байх хугацаанд хадгална. Partners-ийн хүсэлтийг бизнес бүртгэлтэй байх хугацаанд хадгална, учир нь зар нь түүнээс үүссэн. Хандивын бүртгэлийг Сан хүлээн авсан зүйлийнхээ бүртгэлийг хадгалах шаардлагатай хугацаанд хадгална — хандивт баримт олгодог бөгөөд АНУ-д татвар төлдөг хандивлагчийн хувьд энэ нь татварын тайлангийн нэг мөр юм — бичсэн тэмдэглэл тань түүнтэй хамт хадгалагдана. Тэр бүртгэлийн өөрчлөлт бүрийг тэмдэглэдэг бөгөөд тэмдэглэлийг бүртгэлтэй адил хугацаанд хадгална. Ажилтны бүртгэлийг тухайн хүн Сантай ажиллах хугацаанд хадгална. Устгахыг хүсвэл, хадгалах шаардлагагүй бол бид устгана.',
        },
      ],
    },
    {
      heading: { en: 'What you can ask for', mn: 'Та юу хүсэж болох вэ' },
      body: [
        {
          en: 'A copy of what we hold about you, a correction, or its deletion. Write to us and say which; we will answer, and we will not ask you why.',
          mn: 'Бидэнд байгаа таны тухай мэдээллийн хуулбар, засвар, эсвэл түүнийг устгах. Бидэнд бичиж, алийг нь хүсэж буйгаа хэлээрэй; бид хариулах бөгөөд яагаад гэж асуухгүй.',
        },
        {
          en: 'Anyone with an account can change their own details and the language we write to them in, from their own profile, without asking anybody.',
          mn: 'Бүртгэлтэй хүн бүр өөрийн профайлаасаа өөрийн мэдээлэл, мөн бидний түүнд бичих хэлийг хэнээс ч асуухгүйгээр өөрчилж болно.',
        },
      ],
    },
    {
      heading: { en: 'Children', mn: 'Хүүхдүүд' },
      body: [
        {
          en: 'Young people take part in the garage’s training and in Foundation programmes, and that is arranged in person with their families — not through these websites. We do not knowingly collect anything through this platform from a child.',
          mn: 'Залуучууд гаражийн сургалт болон Сангийн хөтөлбөрт оролцдог бөгөөд энэ нь вэбсайтаар бус, гэр бүлтэй нь биечлэн зохицуулагддаг. Бид энэ платформоор дамжуулан хүүхдээс мэдээлэл санаатайгаар цуглуулдаггүй.',
        },
      ],
    },
    {
      heading: { en: 'Changes to this policy', mn: 'Энэ бодлогын өөрчлөлт' },
      body: [
        {
          en: 'The date at the top of this page is when it last changed. It is one document shared by all of our sites, so a change reaches every one of them at once.',
          mn: 'Энэ хуудасны дээд талын огноо бол сүүлд өөрчлөгдсөн огноо. Энэ бол манай бүх сайтын хуваалцдаг нэг баримт бичиг тул өөрчлөлт бүгдэд нь нэгэн зэрэг хүрдэг.',
        },
      ],
    },
  ],
}

export const TERMS: LegalDocument = {
  title: { en: 'Terms and Conditions', mn: 'Үйлчилгээний нөхцөл' },
  updated: '2026-09-19',
  intro: [
    DRAFT_NOTICE,
    {
      en: 'These terms cover the use of every King of Kings Foundation website. Using one of them means accepting them.',
      mn: 'Эдгээр нөхцөл нь King of Kings Foundation-ийн бүх вэбсайтын ашиглалтад хамаарна. Аль нэгийг нь ашиглах нь тэдгээрийг хүлээн зөвшөөрсөнд тооцогдоно.',
    },
  ],
  sections: [
    {
      heading: { en: 'What these sites are', mn: 'Эдгээр сайтууд юу вэ' },
      body: [
        {
          en: 'Information about the Foundation and its work, a directory of businesses in King of Kings Partners, and a way to get in touch about a stay, a tour, a placement or a donation. Where a page carries a form, it says what happens when you send it.',
          mn: 'Сан болон түүний ажлын тухай мэдээлэл, King of Kings Partners-ийн бизнесүүдийн лавлах, мөн байрлах, аялах, дадлага хийх, хандивлах талаар холбогдох арга зам. Хуудсанд маягт байвал илгээхэд юу болохыг хэлдэг.',
        },
      ],
    },
    {
      heading: { en: 'The Partners directory', mn: 'Partners-ийн лавлах' },
      body: [
        {
          en: 'We meet every business before it is listed, and that is what the directory is for — you can trust that somebody has met them. It is not a guarantee of their work, their prices or their availability, and the Foundation is not a party to anything you agree with a member.',
          mn: 'Бид бизнес бүртэй бүртгэхээс өмнө уулздаг бөгөөд лавлахын зорилго яг энэ юм — хэн нэгэн тэдэнтэй уулзсан гэдэгт та итгэж болно. Энэ нь тэдний ажил, үнэ, боломжийн баталгаа биш бөгөөд гишүүнтэй тохиролцсон аливаа зүйлд Сан тал болохгүй.',
        },
        {
          en: 'A member is asked to keep their listing current and to say honestly what they will take someone on for. A listing that has gone stale, or a business that no longer meets what Partners is for, can be paused or removed. We will say why.',
          mn: 'Гишүүнээс зараа шинэчилж байх, хэнийг ямар ажилд авахаа үнэнээр хэлэхийг хүсдэг. Хуучирсан зар, эсвэл Partners-ийн зорилгод нийцэхээ больсон бизнесийг түр зогсоох буюу хасаж болно. Бид шалтгааныг хэлнэ.',
        },
      ],
    },
    {
      heading: { en: 'Accounts', mn: 'Бүртгэл' },
      body: [
        {
          en: 'There is no way to sign yourself up. An account exists because somebody was invited to one — an approved Partners member, or a member of Foundation staff — and it is created when that invitation is followed. Keep your password to yourself; if you think someone else has it, change it, and every session on the account will end.',
          mn: 'Өөрөө бүртгүүлэх боломжгүй. Бүртгэл нь хэн нэгнийг урьсны улмаас үүсдэг — батлагдсан Partners гишүүн, эсвэл Сангийн ажилтан — мөн тэр урилгыг дагаснаар үүснэ. Нууц үгээ өөртөө хадгалаарай; өөр хэн нэгэн мэдсэн гэж бодвол солиорой, тэгвэл тухайн бүртгэлийн бүх сесс дуусна.',
        },
      ],
    },
    {
      heading: { en: 'Giving', mn: 'Хандив' },
      body: [
        {
          en: 'Donations support youth development and keep retreats, camps and community events reachable for people who could not otherwise come. We are not taking payments online yet: where a page invites you to give, it is the start of a conversation, and we will tell you how before you send anything.',
          mn: 'Хандив нь залуучуудын хөгжлийг дэмжиж, цугларалт, зуслан, олон нийтийн арга хэмжээг өөрөөр ирж чадахгүй хүмүүст хүртээмжтэй байлгадаг. Бид одоогоор онлайн төлбөр хүлээн авдаггүй: хуудас танд хандивлахыг санал болгож байвал энэ нь яриа эхлүүлэх явдал бөгөөд юу ч илгээхээс өмнө бид танд яаж хийхийг хэлнэ.',
        },
      ],
    },
    {
      heading: { en: 'Using the sites properly', mn: 'Сайтуудыг зөв ашиглах' },
      body: [
        {
          en: 'Do not use these sites to break the law, to harm or mislead anybody, to send bulk or automated requests through our forms, or to copy the directory for another purpose. Do not attempt to reach the internal portal or any part of the platform you have not been given an account for.',
          mn: 'Эдгээр сайтыг хууль зөрчих, хэн нэгэнд хор хүргэх, төөрөгдүүлэх, манай маягтаар бөөнөөр буюу автоматаар хүсэлт илгээх, эсвэл лавлахыг өөр зорилгоор хуулбарлахад бүү ашиглаарай. Танд бүртгэл өгөөгүй дотоод портал болон платформын аль нэг хэсэгт нэвтрэхийг бүү оролдоорой.',
        },
      ],
    },
    {
      heading: { en: 'What belongs to whom', mn: 'Юу хэнийх вэ' },
      body: [
        {
          en: 'The text, photographs and design of these sites belong to King of Kings Foundation, except where a listing’s own words and pictures belong to the business that wrote them. Scripture is quoted from published translations, which are named where they are quoted.',
          mn: 'Эдгээр сайтын текст, гэрэл зураг, дизайн нь King of Kings Foundation-д харьяалагдана, харин зарын өөрийн үг, зураг нь түүнийг бичсэн бизнест харьяалагдана. Судрыг хэвлэгдсэн орчуулгаас иш татсан бөгөөд иш татсан газарт нь нэрийг нь бичсэн байна.',
        },
      ],
    },
    {
      heading: { en: 'What we can and cannot promise', mn: 'Бид юу амлаж чадах, чадахгүй вэ' },
      body: [
        {
          en: 'We keep these sites accurate and available as best we can, and we will not promise more than that: a page can be out of date, a site can be down, and a business in the directory can turn out to be busy. Anything that matters — a price, a date, whether somebody can take you on — confirm it with us or with them before you rely on it.',
          mn: 'Бид эдгээр сайтыг чадах чинээгээрээ үнэн зөв, ажиллагаатай байлгах бөгөөд түүнээс илүүг амлахгүй: хуудас хуучирч болно, сайт унтарч болно, лавлах дахь бизнес завгүй байж болно. Чухал аливаа зүйлийг — үнэ, огноо, хэн нэгэн таныг авч чадах эсэх — найдахаасаа өмнө бидэнтэй эсвэл тэдэнтэй баталгаажуулаарай.',
        },
      ],
    },
    {
      heading: { en: 'Which law applies', mn: 'Аль хууль үйлчлэх вэ' },
      body: [
        {
          en: 'The law of Mongolia. If something goes wrong, write to us first — almost everything is settled that way, and we would rather settle it that way.',
          mn: 'Монгол Улсын хууль. Хэрэв ямар нэг зүйл буруу болвол эхлээд бидэнд бичээрэй — бараг бүх зүйл ингэж шийдэгддэг бөгөөд бид ч бас ингэж шийдэхийг хүсдэг.',
        },
      ],
    },
    {
      heading: { en: 'Changes, and how to reach us', mn: 'Өөрчлөлт ба бидэнтэй холбогдох' },
      body: [
        {
          en: `The date at the top of this page is when these terms last changed. They are one document shared by all of our sites. Write to ${CONTACT_EMAIL} about anything here.`,
          mn: `Энэ хуудасны дээд талын огноо бол эдгээр нөхцөл сүүлд өөрчлөгдсөн огноо. Энэ бол манай бүх сайтын хуваалцдаг нэг баримт бичиг. Энд байгаа аливаа зүйлийн талаар ${CONTACT_EMAIL} хаягаар бичээрэй.`,
        },
      ],
    },
  ],
}
